import { Link } from "react-router-dom";
import { ArrowRight, Phone, Quote, Calendar, GraduationCap, ShieldCheck, MessageCircle, BookOpen, Users, Award } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import AEOSchema from "../components/AEOSchema";
import { SOCIAL } from "../data/siteData";
import TeamMemberAvatar from "../components/TeamMemberAvatar";
import { personSchema } from "../data/teamData";
import { useTeamData } from "../lib/useTeamData";

export default function Founder() {
    const { founder } = useTeamData();
    const founderPerson = personSchema({
        name: founder.name,
        role: founder.role,
        url: "https://anandtutorials.com/founder",
        image: "https://anandtutorials.com/team/founder.jpg",
        sameAs: founder.sameAs || [],
        bio: Array.isArray(founder.bio) ? founder.bio.join(" ") : founder.bio,
        knowsAbout: [
            "1:1 Home Tuition Operations",
            "Tutor Verification & Onboarding",
            "CBSE / ICSE / IB / IGCSE Curriculum Mapping",
            "Parent Engagement & Learning Outcomes",
            "Online Tutoring Quality Assurance",
            "K-12 Education Policy (India)",
            "Background Verification Standards for Private Tutors",
        ],
        alumniOf: [
            { "@type": "CollegeOrUniversity", "name": "Rani Durgavati University (RDVV), Jabalpur", "sameAs": "https://www.rdunijbpin.org" },
        ],
        birthPlace: "Jabalpur, Madhya Pradesh, India",
        honorificPrefix: "Mr.",
        founderOf: { "@type": "Organization", "@id": "https://anandtutorials.com/#organization", "name": "Anand Tutorials", "url": "https://anandtutorials.com" },
        knowsLanguage: ["en-IN", "hi-IN"],
        awards: [
            "Founded India's most trusted 1:1 home tuition company (2013)",
            "50,000+ students taught across India and 27 countries",
            "Speaker at Indian education forums on private tutor verification & K-12 responsible tutoring",
        ],
    });

    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            founderPerson,
            {
                "@type": "ProfilePage",
                "url": "https://anandtutorials.com/founder",
                "name": `${founder.name} — Founder, Anand Tutorials`,
                "description": `Meet ${founder.name}, founder of Anand Tutorials (est. 2013) — India's leading 1:1 home tuition company with 50,000+ students taught across India and 27 international regions.`,
                "mainEntity": { "@id": "https://anandtutorials.com/founder#person" },
                "about": { "@id": "https://anandtutorials.com/#organization" },
                "dateModified": new Date().toISOString().slice(0, 10),
                "inLanguage": "en-IN",
                "speakable": {
                    "@type": "SpeakableSpecification",
                    "cssSelector": [".aeo-headline", ".aeo-answer"],
                },
            },
        ],
    };

    return (
        <div data-testid="founder-page">
            <SEO
                title={`${founder.name} — Founder, Anand Tutorials | Since 2013`}
                description={`Meet ${founder.name}, founder of Anand Tutorials — India's leading 1:1 home tuition company since 2013. 50,000+ students taught, 2,500+ verified tutors, 27 international regions. Verified background, parent-first operating model.`}
                keywords={`Anand Tutorials founder, ${founder.name}, home tuition founder Jabalpur, 1:1 tutoring founder India, education entrepreneur Jabalpur, Anand Tutorials director, 2013 founded home tuition`}
                path="/founder"
                schema={schema}
                schemas={[founderPerson]}
            />
            <AEOSchema
                isQA
                canonicalUrl="https://anandtutorials.com/founder"
                primaryQuestion={`Who founded Anand Tutorials?`}
                primaryAnswer={`Anand Tutorials was founded in 2013 in Jabalpur, Madhya Pradesh by ${founder.name}, a first-generation education entrepreneur. Under his leadership the company has grown to 50,000+ students taught, 2,500+ verified 1:1 tutors, and operations across India and 27 international regions — all delivered strictly one tutor to one student.`}
                keyFacts={[
                    `${founder.name} founded Anand Tutorials in 2013 in Jabalpur, Madhya Pradesh, India.`,
                    `He is the Founder & Managing Director; personally protects the 1:1 tutor-student ratio and the 4-step tutor verification process.`,
                    `Anand Tutorials has taught 50,000+ students across India + 27 international regions and maintains a 4.8/5 rating across 1,200+ verified parent reviews.`,
                    `The company runs offline home-tuition operations in Jabalpur, Bhopal, Nagpur and 1:1 live online classes worldwide.`,
                ]}
                aboutEntity="Anand Tutorials — Founder"
            />

            <PageHeader
                eyebrow="Founder · Director · Operator"
                title={founder.name}
                subtitle={`Founder & Managing Director of Anand Tutorials since 2013. Personally protects the 1:1 standard, the 4-step tutor verification, and the Sunday parent-report cadence — every single week.`}
            >
                <div className="flex flex-wrap gap-3">
                    <Link to="/register" data-testid="founder-cta-demo" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        Book a Free 1:1 Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href="tel:+917000530316" data-testid="founder-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15">
                        <Phone className="w-4 h-4" /> Call Us
                    </a>
                </div>
            </PageHeader>

            <div className="container mx-auto px-4 py-12 lg:py-16 max-w-5xl">

                {/* Pull quote */}
                <div className="relative bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white rounded-2xl p-8 md:p-12 mb-12">
                    <Quote className="absolute top-6 left-6 w-10 h-10 text-amber-400/40" />
                    <blockquote className="pl-12 md:pl-16 text-xl md:text-2xl font-heading leading-relaxed italic">
                        &ldquo;{founder.quote}&rdquo;
                    </blockquote>
                    <div className="pl-12 md:pl-16 mt-4 text-sm text-amber-200">
                        — {founder.name}, Founder
                    </div>
                </div>

                {/* Founder bio */}
                <section className="grid md:grid-cols-[1fr_2fr] gap-8 mb-14">
                    <aside className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-24 self-start">
                        <div className="flex justify-center mb-4">
                            <TeamMemberAvatar
                                assetKey={founder.photo_asset_key || "founder_photo"}
                                size="w-40 h-40 sm:w-44 sm:h-44 md:w-48 md:h-48 lg:w-52 lg:h-52"
                                priority
                                alt={`${founder.name} — Founder, Anand Tutorials`}
                                ringClass="ring-4 ring-white shadow-xl"
                                fallback={<span className="text-4xl font-heading font-bold text-[#1E3A8A]">{(founder.name || "A").split(" ")[0].charAt(0)}</span>}
                            />
                        </div>
                        <h3 className="text-center font-heading font-bold text-xl text-[#0F172A]">{founder.name}</h3>
                        <p className="text-center text-sm text-slate-500 mb-4">{founder.role}</p>
                        <ul className="text-sm text-slate-600 space-y-2 mt-4">
                            <li className="flex items-start gap-2"><Calendar className="w-4 h-4 mt-0.5 text-[#1E3A8A] shrink-0" /><span>Founded 2013 in Jabalpur, Madhya Pradesh, India</span></li>
                            <li className="flex items-start gap-2"><GraduationCap className="w-4 h-4 mt-0.5 text-[#1E3A8A] shrink-0" /><span>{(founder.education || [])[0] || "B.Tech in Mechanical Engineering"}</span></li>
                            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><span>Verified Indian education operator</span></li>
                        </ul>
                    </aside>

                    <div className="space-y-5">
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A]">The story behind a 12-year 1:1 standard</h2>
                        {(founder.bio || "").split(/\n+/).filter(Boolean).map((p, i) => (
                            <p key={i} className="text-slate-700 leading-relaxed">{p}</p>
                        ))}
                    </div>
                </section>

                {/* Areas of expertise */}
                <section className="mb-14">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mb-6">Areas of expertise</h2>
                    <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                        {(founder.responsibilities || []).map((e, i) => (
                            <div key={i} data-testid={`founder-expertise-${i}`} className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm font-semibold text-[#1E3A8A]">
                                <BookOpen className="w-4 h-4 mb-2 text-blue-600" />
                                {e}
                            </div>
                        ))}
                    </div>
                </section>

                {/* Timeline */}
                <section className="mb-14">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mb-6">Anand Tutorials — the 12-year journey</h2>
                    <ol className="relative border-l-2 border-blue-100 ml-3 space-y-6">
                        {(founder.achievements || []).map((a, i) => (
                            <li key={i} data-testid={`founder-milestone-${i}`} className="ml-6">
                                <span className="absolute -left-3 flex items-center justify-center w-6 h-6 bg-[#1E3A8A] rounded-full text-white text-xs font-bold">
                                    {a.year.slice(-2)}
                                </span>
                                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                                    <div className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1">{a.year}</div>
                                    <div className="text-sm text-slate-700">{a.text}</div>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>

                {/* Why this matters to parents */}
                <section className="bg-slate-50 rounded-2xl p-8 md:p-10 mb-14">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mb-3">Why a named, accountable founder matters</h2>
                    <p className="text-slate-700 leading-relaxed mb-4">
                        Most tutor marketplaces hide behind a brand. There is no human accountable when a verification fails, when a tutor flakes, or when a parent escalates. Anand Tutorials is different — every parent escalation routes to a person, with a name and a number, who reads it.
                    </p>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <Users className="w-5 h-5 text-[#1E3A8A] mb-2" />
                            <div className="font-bold text-sm text-[#0F172A] mb-1">Personally signs off</div>
                            <div className="text-xs text-slate-600">on every NEET / JEE mentor onboarding</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <ShieldCheck className="w-5 h-5 text-emerald-600 mb-2" />
                            <div className="font-bold text-sm text-[#0F172A] mb-1">Reads sample Sunday reports</div>
                            <div className="text-xs text-slate-600">every week to protect quality</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <Award className="w-5 h-5 text-amber-600 mb-2" />
                            <div className="font-bold text-sm text-[#0F172A] mb-1">Owns the panel review</div>
                            <div className="text-xs text-slate-600">tutors who slip below standard exit</div>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section data-testid="founder-cta-section" className="bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white rounded-2xl p-8 md:p-10 text-center">
                    <h3 className="font-heading text-2xl md:text-3xl font-bold mb-3">Speak to a founder-vetted tutor today</h3>
                    <p className="text-blue-100 mb-6 max-w-2xl mx-auto">Every demo at Anand Tutorials is with a verified specialist, not the next-available freelancer. Book your free 1:1 demo class — no card, no commitment.</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link to="/register" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                            Book a Free Demo Class <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/team" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/15">
                            Meet the Team <Users className="w-4 h-4" />
                        </Link>
                        <a href={`https://wa.me/${SOCIAL.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 text-white font-semibold px-6 py-3 rounded-lg hover:bg-emerald-600 transition-colors">
                            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                        </a>
                    </div>
                </section>

            </div>
        </div>
    );
}
